import type { RequestBody, Schemas } from '@suivipma/shared'
import { api, unwrap } from '@/lib/api'

/** Enveloppes typées des routes d'authentification — le contrat fait le typage. */

export type UserProfile = Schemas['UserProfile']
export type SessionResponse = Schemas['SessionResponse']
export type SessionSummary = Schemas['SessionSummary']
export type AuthEvent = Schemas['AuthEvent']

export async function register(body: RequestBody<'register'>): Promise<SessionResponse> {
  return unwrap(await api.POST('/api/auth/register', { body }))
}

export async function login(body: RequestBody<'login'>): Promise<SessionResponse> {
  return unwrap(await api.POST('/api/auth/login', { body }))
}

export type MeResponse = Schemas['MeResponse']

export async function getMe(): Promise<MeResponse> {
  return unwrap(await api.GET('/api/auth/me'))
}

export async function updateMe(body: RequestBody<'updateMe'>): Promise<UserProfile> {
  return unwrap(await api.PATCH('/api/auth/me', { body }))
}

export async function forgotPassword(email: string): Promise<void> {
  unwrap(await api.POST('/api/auth/password/forgot', { body: { email } }))
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  unwrap(await api.POST('/api/auth/password/reset', { body: { token, newPassword } }))
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  unwrap(await api.POST('/api/auth/password/change', { body: { currentPassword, newPassword } }))
}

export async function verifyEmail(token: string): Promise<void> {
  unwrap(await api.POST('/api/auth/email/verify', { body: { token } }))
}

export async function resendEmailVerification(): Promise<void> {
  unwrap(await api.POST('/api/auth/email/verification/resend'))
}

export async function requestEmailChange(currentPassword: string, newEmail: string): Promise<void> {
  unwrap(await api.POST('/api/auth/email/change/request', { body: { currentPassword, newEmail } }))
}

export async function confirmEmailChange(token: string): Promise<void> {
  unwrap(await api.POST('/api/auth/email/change/confirm', { body: { token } }))
}

export async function listSessions(): Promise<SessionSummary[]> {
  return unwrap(await api.GET('/api/auth/sessions'))
}

export async function revokeSession(familyId: string): Promise<void> {
  unwrap(await api.DELETE('/api/auth/sessions/{familyId}', { params: { path: { familyId } } }))
}

export async function logoutOthers(): Promise<void> {
  unwrap(await api.POST('/api/auth/logout-all', { params: { query: { keepCurrent: 'true' } } }))
}

export async function listAuthEvents(limit = 15): Promise<AuthEvent[]> {
  return unwrap(await api.GET('/api/auth/events', { params: { query: { limit } } }))
}

/* ── Second facteur ── */

export type MfaStatus = Schemas['MfaStatus']
export type UpgradedSession = Schemas['UpgradedSession']
export type SessionAssurance = Schemas['SessionAssurance']

export async function mfaStatus(): Promise<MfaStatus> {
  return unwrap(await api.GET('/api/auth/mfa'))
}

export async function totpEnroll(): Promise<Schemas['TotpEnrollment']> {
  return unwrap(await api.POST('/api/auth/mfa/totp/enroll'))
}

export async function totpActivate(code: string): Promise<Schemas['TotpActivation']> {
  return unwrap(await api.POST('/api/auth/mfa/totp/activate', { body: { code } }))
}

export async function totpVerify(code: string): Promise<UpgradedSession> {
  return unwrap(await api.POST('/api/auth/mfa/totp/verify', { body: { code } }))
}

export async function totpDisable(): Promise<void> {
  unwrap(await api.POST('/api/auth/mfa/totp/disable'))
}

export async function emailOtpRequest(): Promise<void> {
  unwrap(await api.POST('/api/auth/mfa/email-otp/request'))
}

export async function emailOtpVerify(code: string): Promise<UpgradedSession> {
  return unwrap(await api.POST('/api/auth/mfa/email-otp/verify', { body: { code } }))
}

export async function backupCodeVerify(code: string): Promise<Schemas['BackupCodeVerification']> {
  return unwrap(await api.POST('/api/auth/mfa/backup/verify', { body: { code } }))
}

export async function backupCodesRegenerate(): Promise<Schemas['BackupCodes']> {
  return unwrap(await api.POST('/api/auth/mfa/backup/regenerate'))
}
